import { Column } from '@tanstack/react-table'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface OrderByProps<TData, TValue> {
  column: Column<TData, TValue>
}

const OrderUpIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="10"
      height="12"
      viewBox="0 0 10 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4.99995 11.0834L5.00004 1.4167M5.00004 1.4167L0.916708 5.50003M5.00004 1.4167L9.08337 5.50003"
        strokeWidth="0.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const OrderDownIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="10"
      height="12"
      viewBox="0 0 10 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M5.00005 0.916626L4.99996 10.5833M4.99996 10.5833L9.08329 6.49997M4.99996 10.5833L0.916626 6.49997"
        strokeWidth="0.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const OrderIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="7"
      height="9"
      viewBox="0 0 7 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.931851 2.43185C0.756115 2.60759 0.756115 2.89251 0.931851 3.06825C1.10759 3.24398 1.39251 3.24398 1.56825 3.06825L3.50005 1.13644L5.43185 3.06825C5.60759 3.24398 5.89251 3.24398 6.06825 3.06825C6.24398 2.89251 6.24398 2.60759 6.06825 2.43185L3.81825 0.181851C3.73386 0.0974594 3.6194 0.0500488 3.50005 0.0500488C3.3807 0.0500488 3.26624 0.0974595 3.18185 0.181851L0.931851 2.43185ZM6.06825 6.56825C6.24398 6.39251 6.24398 6.10759 6.06825 5.93185C5.89251 5.75612 5.60759 5.75612 5.43185 5.93185L3.50005 7.86366L1.56825 5.93185C1.39251 5.75612 1.10759 5.75612 0.931851 5.93185C0.756115 6.10759 0.756115 6.39251 0.931851 6.56825L3.18185 8.81825C3.35759 8.99399 3.64251 8.99399 3.81825 8.81825L6.06825 6.56825Z"
        fill="#4F4F4F"
        strokeWidth="0.9"
      />
    </svg>
  )
}

const TrashIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M1 4H13" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M11.6668 4V13.3333C11.6668 14 11.0002 14.6667 10.3335 14.6667H3.66683C3.00016 14.6667 2.3335 14 2.3335 13.3333V4"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.3335 4.00016V2.66683C4.3335 2.00016 5.00016 1.3335 5.66683 1.3335H8.3335C9.00016 1.3335 9.66683 2.00016 9.66683 2.66683V4.00016"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.6665 7.3335V11.3335"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.3335 7.3335V11.3335"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function OrderBy<TData, TValue>({ column }: OrderByProps<TData, TValue>) {
  const direction = column.getIsSorted() || 'none'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={'h-fit rounded-md p-2 hover:bg-[#F2F2F2]'}>
        {
          {
            asc: <OrderUpIcon className="stroke-[#4F4F4F]" />,
            desc: <OrderDownIcon className="stroke-[#4F4F4F]" />,
            none: <OrderIcon />,
          }[direction]
        }
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[250px] min-w-fit text-[14px] font-light text-primary bg-white">
        <DropdownMenuItem onClick={() => column.toggleSorting(false, true)}>
          <div className="flex gap-2 items-center text-primary text-[14px]">
            <OrderUpIcon className="stroke-[#4F4F4F]" />
            Ascendente
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => column.toggleSorting(true, true)}>
          <div className="flex gap-2 items-center text-primary text-[14px]">
            <OrderDownIcon className="stroke-[#4F4F4F]" />
            Descendente
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => column.clearSorting()}>
          <div className="flex gap-2 items-center text-primary text-[14px]">
            <TrashIcon className="stroke-[#4F4F4F]" />
            Borrar orden
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { OrderBy }
